import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, RefreshCw, KeyRound, CheckCircle2, MoreHorizontal, LayoutDashboard, Shirt, Sparkles, FileText, Users as UsersIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import InstallHint from "./InstallHint";
import IdleWarning from "./IdleWarning";

const NAV = [
  { id: "dashboard", to: "/", end: true, label: "Dashboard", Icon: LayoutDashboard },
  { id: "tees", to: "/tees", label: "Printed Tees", Icon: Shirt },
  { id: "analysis", to: "/analysis", label: "Analysis", Icon: Sparkles },
  { id: "reports", to: "/reports", label: "Reports", Icon: FileText },
  { id: "users", to: "/users", label: "Users", Icon: UsersIcon, owner: true },
];
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import ChangePasswordModal from "./ChangePasswordModal";

export default function Layout() {
  const { user, logout } = useAuth();
  const [sync, setSync] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [ls, setLs] = useState(null);
  const [lsMsg, setLsMsg] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  async function refreshStatus() {
    try {
      const { data } = await api.get("/sync/status");
      setSync(data);
    } catch (e) {}
  }

  async function refreshLsStatus() {
    try {
      const { data } = await api.get("/auth/lightspeed/status");
      setLs(data);
    } catch (e) {}
  }

  useEffect(() => {
    refreshStatus();
    refreshLsStatus();
    const t = setInterval(refreshStatus, 5000);
    // Surface the OAuth return status, then strip it from the URL.
    const q = new URLSearchParams(window.location.search);
    if (q.get("lightspeed")) {
      setLsMsg(q.get("lightspeed") === "connected"
        ? { ok: true, text: "Lightspeed connected via OAuth." }
        : { ok: false, text: `Lightspeed connection failed (${q.get("reason") || "unknown"}).` });
      refreshLsStatus();
      const url = new URL(window.location.href);
      url.searchParams.delete("lightspeed");
      url.searchParams.delete("reason");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
    return () => clearInterval(t);
  }, []);

  async function startSync() {
    await api.post("/sync/start");
    refreshStatus();
  }

  async function connectLightspeed() {
    try {
      const { data } = await api.get("/auth/lightspeed/start");
      // Lightspeed's login refuses to be framed (X-Frame-Options / CSP
      // frame-ancestors), so we must open it in a real top-level tab rather
      // than navigating the preview iframe.
      const win = window.open(data.authorize_url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Popup blocked — fall back to breaking out of the iframe to the top window.
        try { window.top.location.assign(data.authorize_url); }
        catch { window.location.assign(data.authorize_url); }
        return;
      }
      // Poll connection status so the header updates once approval completes.
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const { data: st } = await api.get("/auth/lightspeed/status");
          if (st?.connected) {
            setLs(st);
            setLsMsg({ ok: true, text: "Lightspeed connected via OAuth." });
            clearInterval(poll);
          }
        } catch (e) {}
        if (tries > 60) clearInterval(poll);
      }, 3000);
    } catch (e) {
      setLsMsg({ ok: false, text: "Could not start Lightspeed authorization." });
    }
  }

  const syncing = sync?.status === "running";
  const phase = sync?.phase;
  const salesSynced = sync?.sales_synced ?? 0;

  // Human "last synced" label from the most recent successful completion.
  const lastSyncTs = sync?.finished_at || sync?.updated_at;
  function lastSyncedLabel(ts) {
    if (!ts) return null;
    const d = new Date(ts);
    if (isNaN(d)) return null;
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    let rel;
    if (diffMin < 1) rel = "just now";
    else if (diffMin < 60) rel = `${diffMin}m ago`;
    else if (diffMin < 1440) rel = `${Math.floor(diffMin / 60)}h ago`;
    else rel = `${Math.floor(diffMin / 1440)}d ago`;
    const abs = d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    return { rel, abs };
  }
  const lastSynced = lastSyncedLabel(lastSyncTs);

  return (
    <div className="min-h-screen bg-[#f5f2eb] text-[#0a0a0a] overflow-x-hidden" data-testid="app-shell">
      <div className="bm-thick" />

      {/* Masthead */}
      <header className="px-4 md:px-6 lg:px-10 py-3 md:py-5 border-b border-[#0a0a0a] flex items-center justify-between gap-3">
        <div className="flex items-center gap-6 min-w-0">
          <NavLink to="/" className="flex flex-col items-start leading-none shrink-0" data-testid="header-logo">
            <img src="/brand/bm-wordmark-dark.png" alt="Black Market" className="h-4 md:h-5 w-auto" draggable={false} />
            <span className="bm-headline italic font-normal text-[#c72c2c] text-base md:text-lg mt-1">/ insights</span>
          </NavLink>
          <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
            {NAV.filter((n) => !n.owner || user?.role === "owner").map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.id}`}
                className={({ isActive }) =>
                  `bm-eyebrow px-3 py-1.5 border ${
                    isActive
                      ? "bg-[#0a0a0a] text-[#f5f2eb] border-[#0a0a0a]"
                      : "border-transparent hover:border-[#0a0a0a]/40 text-neutral-600"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Desktop actions */}
        <div className="hidden md:flex items-center gap-4">
          <div className="flex flex-col items-start">
            <button
              onClick={startSync}
              disabled={syncing || user?.role !== "owner"}
              data-testid="sync-button"
              className="bm-eyebrow px-3 py-2 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-[#f5f2eb] disabled:opacity-60 flex items-center gap-2"
            >
              <RefreshCw size={12} className={syncing ? "animate-spin" : ""} />
              {syncing
                ? `Syncing ${phase}… ${(sync?.sales_scanned ?? salesSynced).toLocaleString()} scanned / ${salesSynced.toLocaleString()} kept`
                : "Sync from POS"}
            </button>
            {!syncing && lastSynced && (
              <span
                data-testid="last-synced"
                title={`Last synced ${lastSynced.abs}`}
                className="bm-eyebrow text-neutral-500 mt-1 leading-none"
              >
                Last synced · {lastSynced.rel}{sync?.last_trigger === "nightly" ? " (auto)" : ""} · nightly 4 AM
              </span>
            )}
          </div>
          <div className="bm-eyebrow hidden lg:block text-neutral-600">
            {user?.email}
          </div>
          <button
            onClick={() => setPwOpen(true)}
            data-testid="change-password-button"
            className="bm-eyebrow flex items-center gap-1 hover:text-[#c72c2c]"
            title="Change password"
          >
            <KeyRound size={14} /> Password
          </button>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="bm-eyebrow flex items-center gap-1 hover:text-[#c72c2c]"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>

        {/* Phone: overflow menu */}
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-button"
              aria-label="Menu"
              className="md:hidden inline-flex items-center justify-center w-11 h-11 border border-[#0a0a0a] hover:bg-[#0a0a0a]/5"
            >
              <MoreHorizontal size={18} />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[#f5f2eb] border-l-2 border-[#0a0a0a] w-[86vw] max-w-sm p-0" data-testid="mobile-menu">
            <div className="px-5 pt-5 pb-4 border-b border-[#0a0a0a]">
              <img src="/brand/bm-wordmark-dark.png" alt="Black Market" className="h-4 w-auto" draggable={false} />
              <div className="bm-eyebrow text-neutral-500 mt-2 break-all" data-testid="mobile-menu-email">{user?.email}</div>
            </div>
            <div className="divide-y divide-[#0a0a0a]/15">
              {user?.role === "owner" && (
                <button
                  onClick={() => { startSync(); setMenuOpen(false); }}
                  disabled={syncing}
                  data-testid="mobile-sync-button"
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[#0a0a0a]/5 disabled:opacity-60"
                >
                  <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{syncing ? `Syncing ${phase}…` : "Sync from POS"}</span>
                    {lastSynced && <span className="block bm-eyebrow text-neutral-500 mt-0.5">Last synced · {lastSynced.rel} · nightly 4 AM</span>}
                  </span>
                </button>
              )}
              <button
                onClick={() => { setMenuOpen(false); setPwOpen(true); }}
                data-testid="mobile-change-password-button"
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[#0a0a0a]/5"
              >
                <KeyRound size={16} /> <span className="text-sm font-medium">Change password</span>
              </button>
              <button
                onClick={logout}
                data-testid="mobile-logout-button"
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[#0a0a0a]/5 text-[#c72c2c]"
              >
                <LogOut size={16} /> <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} noPassword={user?.no_password} />

      {/* Lightspeed OAuth return message */}
      {lsMsg && (
        <div
          data-testid="lightspeed-message"
          className={`px-6 lg:px-10 py-3 bm-eyebrow flex items-center justify-between ${lsMsg.ok ? "bg-emerald-700 text-white" : "bg-[#c72c2c] text-white"}`}
        >
          <span className="flex items-center gap-2">
            {lsMsg.ok && <CheckCircle2 size={14} />} {lsMsg.text}
          </span>
          <button onClick={() => setLsMsg(null)} className="underline hover:opacity-80">Dismiss</button>
        </div>
      )}

      {/* Lightspeed disconnected / revoked — persistent until reconnected */}
      {ls?.revoked && (
        <div
          data-testid="lightspeed-revoked-banner"
          className="bg-[#c72c2c] text-white px-6 lg:px-10 py-3 bm-eyebrow flex items-center justify-between gap-3"
        >
          <span>Lightspeed disconnected — new syncs will fail until you reconnect{ls.reason ? ` · ${ls.reason}` : ""}.</span>
          <button onClick={connectLightspeed} data-testid="lightspeed-reconnect" className="underline hover:opacity-80 whitespace-nowrap">Reconnect →</button>
        </div>
      )}

      {/* Sync banner */}
      {sync?.status === "never_run" && (
        <div data-testid="sync-banner" className="bg-[#0a0a0a] text-[#f5f2eb] px-6 lg:px-10 py-3 flex items-center justify-between">
          <div className="bm-eyebrow">Data desk empty. Run your first Lightspeed sync to pull 3 years of receipts.</div>
          <button onClick={startSync} className="bm-eyebrow underline hover:text-[#c72c2c]">Start sync →</button>
        </div>
      )}
      {sync?.status === "error" && (
        <div className="bg-[#c72c2c] text-white px-6 lg:px-10 py-3 bm-eyebrow">
          Sync error: {sync?.error}
        </div>
      )}
      {syncing && (
        <div className="bg-[#0a0a0a] text-[#f5f2eb] px-6 lg:px-10 py-2 bm-eyebrow">
          Sync in progress · phase: {phase} · {salesSynced.toLocaleString()} sales · {(sync?.line_items_synced ?? 0).toLocaleString()} line items
        </div>
      )}

      <main className="px-4 md:px-6 lg:px-10 py-6 md:py-8 pb-28 md:pb-8">
        <Outlet />
      </main>

      {/* Phone: bottom tab bar */}
      <IdleWarning />
      <InstallHint />
      <nav
        data-testid="mobile-tab-bar"
        className="md:hidden fixed inset-x-0 bottom-0 z-30 bg-[#f5f2eb] border-t-2 border-[#0a0a0a] flex"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {NAV.filter((n) => !n.owner || user?.role === "owner").map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`tab-${n.id}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] ${
                isActive ? "text-[#c72c2c]" : "text-neutral-600"
              }`
            }
          >
            <n.Icon size={18} strokeWidth={2} />
            <span className="bm-eyebrow !text-[9px] !tracking-[0.1em]">{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <footer className="border-t border-[#0a0a0a] mt-16 px-4 md:px-6 lg:px-10 py-6 bm-eyebrow text-neutral-500 flex flex-wrap gap-2 justify-between">
        <div>&copy; 2026 Black Market Vintage · Read-only Lightspeed connection</div>
        <div>Powered by Claude</div>
      </footer>
    </div>
  );
}
